import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/ui/page";
import { ClientForm } from "@/components/ClientForm";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fmtCurrency } from "@/lib/format";

const EMPTY = { business_name: "", monthly_vsm_fee: 0, average_job_size: 0, status: "Active" };

export default function Clients() {
  const qc = useQueryClient();
  const { data: clients = [], isLoading } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["clients"] });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/clients/${id}`)).data,
    onSuccess: () => { toast.success("Client deleted"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader title="Clients" subtitle="Manage VSM client workspaces">
        <ClientDialog onDone={refresh} />
      </PageHeader>

      {isLoading ? <Spinner /> : clients.length === 0 ? (
        <EmptyState icon={Building2} title="No clients yet" subtitle="Add your first VSM client workspace." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50"><TableHead>Business</TableHead><TableHead>Industry</TableHead><TableHead>Contacts</TableHead><TableHead>Deals</TableHead><TableHead>Avg Job</TableHead><TableHead>Monthly Fee</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                  <TableCell className="font-semibold text-slate-900">{c.business_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{c.industry || "—"}</TableCell>
                  <TableCell>{c.contact_count}</TableCell>
                  <TableCell>{c.deal_count}</TableCell>
                  <TableCell className="tabular-nums">{fmtCurrency(c.average_job_size)}</TableCell>
                  <TableCell className="tabular-nums">{fmtCurrency(c.monthly_vsm_fee)}</TableCell>
                  <TableCell><Badge variant="outline" className={c.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : ""}>{c.status}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <ClientDialog client={c} onDone={refresh} trigger={<Button variant="ghost" size="sm" data-testid={`edit-client-${c.id}`}><Pencil className="h-4 w-4" /></Button>} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-rose-600" data-testid={`delete-client-${c.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete {c.business_name}?</AlertDialogTitle><AlertDialogDescription>This permanently removes the client and all its contacts, activities, deals and referrals.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-rose-600" onClick={() => del.mutate(c.id)} data-testid="confirm-delete-client">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function ClientDialog({ client, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(client || EMPTY);
  const isEdit = !!client;
  const mut = useMutation({
    mutationFn: async () => isEdit
      ? (await api.put(`/clients/${client.id}`, form)).data
      : (await api.post("/clients", form)).data,
    onSuccess: () => { toast.success(isEdit ? "Client updated" : "Client created"); setOpen(false); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(client || EMPTY); }}>
      <DialogTrigger asChild>{trigger || <Button data-testid="add-client-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Client</Button>}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Client" : "New Client"}</DialogTitle></DialogHeader>
        <ClientForm value={form} onChange={setForm} />
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={!form.business_name || mut.isPending} data-testid="save-client-btn">{isEdit ? "Save Changes" : "Create Client"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
